import { compileTemplate as compileTemplateInternal } from "./compile/compileTemplate";
import { layoutTemplate } from "./layout/layoutEngine";
import { interpolateVariables } from "./render/interpolate";
import { emitZpl } from "./render/zplEmitter";
import type {
  CompileResult,
  RenderContext,
  RenderResult,
} from "./types/render";
import type { CompiledTemplate, LabelTemplateV2 } from "./types/template";
import { mmToDots } from "./utils/units";

export {
  MissingVariableError,
  RenderLayoutError,
  SchemaVersionError,
  TemplateValidationError,
} from "./errors";
export type {
  CompileResult,
  EngineWarning,
  PrinterProfile,
  RenderContext,
  RenderDiagnostics,
  RenderResult,
  TextEngineMode,
  TextRenderDiagnostic,
  WarningCode,
  ZebraDpi,
} from "./types/render";
export type {
  Anchor,
  BarcodeElementTemplate,
  BarcodeTemplate,
  BarcodeWidthPolicy,
  BarcodeWidthPolicyFitInside,
  BarcodeWidthPolicyFixedModule,
  BoxElementTemplate,
  CompiledTemplate,
  LabelContainerTemplate,
  LabelElementTemplate,
  LabelTemplateV2,
  LineElementTemplate,
  MainJustify,
  OverflowPolicy,
  Spacing,
  SpacingInput,
  TemplateVariableDefinition,
  TemplateVariableType,
  TextElementTemplate,
  TextFitMode,
  TextOverflowStrategy,
  TextPresencePolicy,
  TextTransform,
  ZplEncoding,
} from "./types/template";

export function compileTemplate(template: LabelTemplateV2): CompileResult {
  return compileTemplateInternal(template);
}

export function renderTemplate(
  compiled: CompiledTemplate,
  vars: Record<string, unknown>,
  ctx: RenderContext,
): RenderResult {
  const interpolation = interpolateVariables(compiled, vars);
  const layout = layoutTemplate(
    compiled,
    interpolation.valuesByElementId,
    interpolation.hiddenElementIds,
    ctx,
  );
  const encoding = ctx.encoding ?? compiled.defaults.encoding;
  const emitted = emitZpl(layout, ctx, encoding);

  return {
    zpl: emitted.zpl,
    warnings: emitted.warnings,
    diagnostics: {
      barcode: emitted.barcodeDiagnostics,
      layout: layout.diagnostics,
      text: emitted.textDiagnostics,
      hiddenElements: Array.from(interpolation.hiddenElementIds).sort(),
    },
    metrics: {
      widthDots: mmToDots(compiled.canvas.widthMm, ctx.dpi),
      heightDots: mmToDots(compiled.canvas.heightMm, ctx.dpi),
      renderedElementCount: layout.elements.length,
    },
  };
}

export function renderTemplateFromJson(
  template: LabelTemplateV2,
  vars: Record<string, unknown>,
  ctx: RenderContext,
): RenderResult {
  const compileResult = compileTemplateInternal(template);
  const renderResult = renderTemplate(compileResult.compiled, vars, ctx);

  return {
    ...renderResult,
    warnings: [...compileResult.warnings, ...renderResult.warnings],
  };
}
